import { Slide, toast } from "react-toastify";
import {
  locationPermissionsLocalStorageKey,
  mapHiddenWhereCategory,
  pageSize,
  refugeRestroomsCategory,
  runInDummyDataMode,
  showAllZoomLevel,
  subfeatures,
  writeToLog,
} from "../const";
import {
  fetchCategoryForPoiCat,
  fetchLocationRatingSummaries,
  listLocations,
  listPoiCategories,
  mapboxSuggest,
  searchByCategoryOrKeyword,
  SearchByCategoryOrKeywordData,
  searchForCertifiedOnly,
  searchForOnlineOnly,
  searchForShaneRatedOnly,
} from "../client";
import { dummyBathroomData, dummySearchResults } from "../dummy-data";
import { LngLatBounds, Marker } from "mapbox-gl";
import { getTinyIntAsBoolean } from "../functions/Functions";
import { resultsBlockedError, resultsFilteredError } from "../strings";
import {
  CustomMarker,
  FilterSet,
  Rating,
  SearchResult,
  SearchSet,
} from "../types/types";
import { useShaneClientContext } from "../core/shaneClient/ShaneClientProvider";
import { useAppState } from "../core/appState/UseAppStateZustand";
import { useCallback, useState } from "react";
import { tealBackground, tealText } from "@shane-collective/design-system";

export function useMapFunctions() {
  const { shaneClient } = useShaneClientContext();
  const {
    setLastMoveAction,
    setMovingAutomatically,
    setSelectedFeature,
    setResultsPosition,
    initialRRLngLat,
    isSmallScreen: small,
    setPartial,
  } = useAppState.getState();

  const searchResultsToGeoJson = (results: SearchResult[]) => {
    let geojson: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: [],
    };
    let features = [];

    for (const r of results) {

      features.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [r.lng, r.lat],
        },
        properties: {
          name: r.locationName,
          address: r.locationAddress,
          id: r.locationId,
          ratings: r.ratings,
          ratingAll: (r as any)?.ratingAll ?? null,
          ratingLikeMe: (r as any)?.ratingLikeMe ?? null,
          isCertified:
            typeof r.isCertified === "number"
              ? r.isCertified === 1
              : r.isCertified,
          isShaneLocation:
            typeof r.isShaneLocation === "number"
              ? r.isShaneLocation === 1
              : r.isShaneLocation,
          priority:
            r?.source === "refuge-restrooms"
              ? "superhigh"
              : !r.isShaneLocation
              ? "low"
              : r.isCertified
              ? "high"
              : r?.isPromoted
              ? "superhigh"
              : "medium",
          isPromoted:
            typeof r?.isPromoted === "number"
              ? r?.isPromoted === 1
              : r?.isPromoted ?? false,
          category: r.locationCategory,
          mapboxId: r.mapboxId ?? "",
          source: r?.source,
          notes: r?.notes,
          tags:
            typeof r?.tags === "string" ? JSON.parse(r?.tags) : r?.tags ?? [],
          description: r?.locationDescription,
          customIcon: (r as any)?.customIcon,
          rrId: (r as any)?.rrId,
          externalId: (r as any)?.externalId,
          isPoi: Object.keys(r as any).includes('bbox') ? true : false
        },
      });
    }

    //@ts-ignore
    geojson.features = features; //FIXME - fix properly!

    return geojson;
  };

  const setMarkerVisibility = useCallback((marker: any, isVisible: boolean) => {
    const element = marker.marker.getElement();

    if (isVisible) {
      element.classList.remove("marker-hidden");
    } else {
      element.classList.add("marker-hidden");
    }
  }, []);

  const handleMapMove = async (
    map: mapboxgl.Map,
    markers: CustomMarker[],
    useClustering: boolean,
    zoomLevelBeforeMove: number,
    unhideSearchThisAreaButton: () => void
  ) => {
  
    writeToLog([
      "[UseMapFunctions] running handleMapMove",
      map,
      zoomLevelBeforeMove,
      map.getZoom(),
    ]);

    setLastMoveAction("none");
    let newMarkers: CustomMarker[] = [];
    if (map) {
      if (markers) {
        if (markers.length === 1) {
          //!from mapbox search box, don't apply any visibility overrides
          setMarkerVisibility(markers[0], true);
          const center = map.getCenter();
          const zoomLevel = map.getZoom();

          return {
            markers,
            lng: parseFloat(center.lng.toFixed(4)),
            lat: parseFloat(center.lat.toFixed(4)),
            zoom: parseFloat(zoomLevel.toFixed(2)),
          };
        }
        if (useClustering) {
          // Get all clusters in the current view
          const clusters = map.querySourceFeatures("markers", {
            filter: ["has", "point_count"], // Filter for clusters only
          });

          let tempMarkers1 = markers;

          let hiddenMarkers: CustomMarker[] = [];

          // update all markers to set visible / invisible
          const clusterPromises = clusters.map((c) => {
            return new Promise((resolve, reject) => {
              //@ts-ignore
              map
                .getSource("markers")
                //@ts-ignore
                .getClusterLeaves(
                  c.id,
                  2000,
                  0,
                  function (err: any, aFeatures: any) {
                    if (err) {
                      reject(err);
                    } else {
                      resolve(aFeatures);
                    }
                  }
                );
            });
          });

          Promise.all(clusterPromises)
            .then((results) => {
              hiddenMarkers = results.flat() as CustomMarker[];

              tempMarkers1 = tempMarkers1.map((item) => {
                const isInArray2 = hiddenMarkers.some(
                  (target) => target.properties.id === item.id
                );

                return {
                  ...item,
                  isVisible: !isInArray2,
                };
              });

              // set as hidden
              for (const m of tempMarkers1) {
                setMarkerVisibility(m, m?.isVisible ?? false);
              }

              newMarkers = tempMarkers1;
            })
            .catch((error) => {
              console.error("Error fetching cluster leaves:", error);
            });
        } else {
          // logic to display subset of markers based on zoom level
          let clusters = map.querySourceFeatures("markers", {
            filter: ["has", "point_count"], // Filter for clusters only
          });

          // remove duplicate clusters
          clusters = clusters.filter(
            (item, index, self) =>
              index === self.findIndex((t) => t.id === item.id)
          );

          //! handle viewport - set those outside of the viewport as hidden and don't process below
          const bounds = map.getBounds();
          const visibleMarkers = markers.filter((m) => {
            const markerLngLat = m.marker.getLngLat();

            if (bounds) {
              return bounds.contains(markerLngLat);
            } else {
              return false;
            }
          });

          const visibleMarkerIds = visibleMarkers.map((m: any) => {
            return m.id;
          });

          writeToLog([
            "[UseMapFunctions] [handleMapMove] visible marker count",
            visibleMarkerIds.length.toString(),
          ]);

          // hide everything that's outside of visible bounds
          for (const m of visibleMarkers.filter(
            (m: any) => !visibleMarkerIds.includes(m.id)
          )) {
            setMarkerVisibility(m, false);
          }

          if (
            zoomLevelBeforeMove.toFixed(1) === map.getZoom().toFixed(1) ||
            (zoomLevelBeforeMove > 14 && map.getZoom() > 14)
          ) {
            writeToLog([
              "[UseMapFunctions] [handleMapMove] zoom has not changed, exiting",
              Math.floor(map.getZoom()).toString(),
              Math.floor(zoomLevelBeforeMove).toString(),
            ]);

            // IF THIS IS THE FIRST RUN AND THE ZOOM HASN'T CHANGED, NEED TO CALCULATE VISIBILITY FOR CURRENT ZOOM BEFORE DOING THIS FILTERING
            if (markers.filter((m: any) => m?.show).length > 0) {
              // if zoom hasn't changed, assume viewport has, set those to visible which have now entered the visible bounds and have show = true
              for (const m of markers.filter(
                (m: any) => visibleMarkerIds.includes(m.id) && m.show
              )) {
                setMarkerVisibility(m, true);
              }
              const center = map.getCenter();
              const zoomLevel = map.getZoom();

              unhideSearchThisAreaButton();

              return {
                markers,
                lng: parseFloat(center.lng.toFixed(4)),
                lat: parseFloat(center.lat.toFixed(4)),
                zoom: parseFloat(zoomLevel.toFixed(2)),
              };
            }
          } else {
            writeToLog([
              "[UseMapFunctions] [handleMapMove]",
              "zoom changed",
              "original",
              zoomLevelBeforeMove.toString(),
              "new",
              map.getZoom().toString(),
            ]);
          }

          //Max zoom level, show all
          if (Math.floor(map.getZoom()) >= showAllZoomLevel) {
            writeToLog([
              "[UseMapFunctions] [handleMapMove] At max zoom, SHOW ALL",
            ]);
            let tempMarkers1 = markers.map((item) => ({
              ...item,
              show: true,
            }));

            //show everything within visible bounds
            for (const m of visibleMarkers) {
              setMarkerVisibility(m, true);
            }

            const center = map.getCenter();
            const zoomLevel = map.getZoom();

            unhideSearchThisAreaButton();

            return {
              markers: tempMarkers1,
              lng: parseFloat(center.lng.toFixed(4)),
              lat: parseFloat(center.lat.toFixed(4)),
              zoom: parseFloat(zoomLevel.toFixed(2)),
            };
          }

          let zoomDirection = "in";

          if (zoomLevelBeforeMove.toFixed(1) > map.getZoom().toFixed(1)) {
            zoomDirection = "out";
          }

          // update all markers to set visible / invisible
          const clusterPromises = clusters.map((c) => {
            return new Promise((resolve, reject) => {
              //@ts-ignore
              map
                .getSource("markers")
                //@ts-ignore
                .getClusterLeaves(
                  c.id,
                  2000,
                  0,
                  function (err: any, aFeatures: any) {
                    if (err) {
                      reject(err);
                    } else {
                      resolve(
                        aFeatures.map((f: any) => {
                          return { ...f, clusterId: c.id };
                        })
                      );
                    }
                  }
                );
            });
          });

          let clusteredMarkers = await Promise.all(clusterPromises).then(
            (results) => {
              // remove duplicates - some markers seem to be leaves of >1 clusters for whatever reason :/
              let markers = results.flat() as CustomMarker[];

              return Array.from(
                markers
                  .reduce((map, item) => {
                    const id = item.properties.id;
                    if (!map.has(id)) {
                      map.set(id, item);
                    }
                    return map;
                  }, new Map())
                  .values()
              );
            }
          );

          let currentZoom = map?.getZoom() ?? 0;

          let displayPercentage = 1;

          if (currentZoom < 1) {
            displayPercentage = 0.1;
          } else if (currentZoom < 2) {
            displayPercentage = 0.2;
          } else if (currentZoom < 3) {
            displayPercentage = 0.3;
          } else if (currentZoom < 4) {
            displayPercentage = 0.3;
          } else if (currentZoom < 5) {
            displayPercentage = 0.4;
          } else if (currentZoom < 6) {
            displayPercentage = 0.4;
          } else if (currentZoom < 7) {
            displayPercentage = 0.4;
          } else if (currentZoom < 8) {
            displayPercentage = 0.5;
          } else if (currentZoom < 9) {
            displayPercentage = 0.5;
          } else if (currentZoom < 10) {
            displayPercentage = 0.5;
          } else if (currentZoom < 10.5) {
            displayPercentage = 0.5;
          } else if (currentZoom < 11) {
            displayPercentage = 0.5;
          } else if (currentZoom < 12) {
            displayPercentage = 0.6;
          } else if (currentZoom < 13) {
            displayPercentage = 0.7;
          } else if (currentZoom < 14) {
            displayPercentage = 0.8;
          } else {
            displayPercentage = 1;
          }

          if (visibleMarkers.length <= 50) {
            //TODO this needs to be dependent on zoom level and size of viewport?
            displayPercentage = 1;
          }

          if (displayPercentage === 1) {
            let tempMarkers1 = markers.map((item) => ({
              ...item,
              show: true,
            }));

            //show everything within visible bounds
            for (const m of visibleMarkers) {
              setMarkerVisibility(m, true);
            }

            const center = map.getCenter();
            const zoomLevel = map.getZoom();

            unhideSearchThisAreaButton();

            return {
              markers: tempMarkers1,
              lng: parseFloat(center.lng.toFixed(4)),
              lat: parseFloat(center.lat.toFixed(4)),
              zoom: parseFloat(zoomLevel.toFixed(2)),
            };
          }

          let tempMarkers: CustomMarker[] = [];

          const currentMarkers = visibleMarkers;

          for (const c of clusters) {
            const markersInCluster = clusteredMarkers
              .filter((m: any) => m.clusterId === c.id)
              .map((n: any) => n.properties.id);

            let filteredMarkers = currentMarkers
              .filter((item: any) => markersInCluster.includes(item.id))
              .sort((a, b) => (a.show === b.show ? 0 : a.show ? -1 : 1)); // sort here so that those already visible are at the start of the array

            const filterForPriority = (
              markersToFilter: CustomMarker[],
              currentZoom: number
            ) => {
              // everything is low priority because it's all mapbox stuff, we need to allow for this
              return markersToFilter.filter((f) => {
                let isVisible = false;

                if (
                  currentZoom > 12 &&
                  ["superhigh", "high", "medium", "low"].includes(f.priority)
                ) {
                  isVisible = true;
                } else if (
                  currentZoom > 10 &&
                  ["superhigh", "high", "medium"].includes(f.priority)
                ) {
                  isVisible = true;
                } else if (
                  currentZoom > 8 &&
                  ["superhigh", "high"].includes(f.priority)
                ) {
                  isVisible = true;
                } else if (
                  currentZoom > 6 &&
                  ["superhigh"].includes(f.priority)
                ) {
                  isVisible = true;
                }

                // if zoomDirection = in, make sure that nothing which was originally visible is now hidden
                if (!isVisible && zoomDirection === "in") {
                  isVisible = f?.show ?? false;
                }

                return isVisible;
              });
            };

            //all markers which are in the cluster which would be visible based on zoom level
            let twiceFilteredMarkers = filterForPriority(
              filteredMarkers,
              currentZoom
            );

            if (
              displayPercentage * markersInCluster.length >
              twiceFilteredMarkers.length
            ) {
              // add some of the lower priority items in
              twiceFilteredMarkers = twiceFilteredMarkers.concat(
                filteredMarkers
                  .filter((item) => !twiceFilteredMarkers.includes(item))
                  .sort(() => Math.random() - 0.5)
                  .slice(
                    0,
                    displayPercentage * markersInCluster.length -
                      twiceFilteredMarkers.length
                  )
              );
            }

            let nextZoom = currentZoom + 1;

            while (twiceFilteredMarkers.length === 0 && nextZoom < 14) {
              writeToLog([
                "[UseMapFunctions] [handleMapMove] nothing found, trying again",
              ]);
              //nothing found, show some for the next zoom level instead
              twiceFilteredMarkers = filterForPriority(
                filteredMarkers,
                nextZoom
              );
              nextZoom = nextZoom + 1;
            }

            const numToShow = Math.max(
              Math.floor(
                Math.min(markersInCluster.length, 30) * displayPercentage
              ),
              1
            );

            tempMarkers = tempMarkers.concat(
              twiceFilteredMarkers.map((f: any, index: number) => {
                return { ...f, show: numToShow > index };
              })
            );
          }

          // get those which aren't in tempMarkers
          const tempMarkersIds = tempMarkers.map((m: any) => {
            return m.id;
          });
          let tempMarkers1 = currentMarkers.filter(
            (m: any) => !tempMarkersIds.includes(m.id)
          );

          tempMarkers = tempMarkers.concat(
            tempMarkers1.map((f) => {
              let isVisible = false;

              if (currentZoom > 14) {
                isVisible = true;
              } else if (
                currentZoom > 12 &&
                ["superhigh", "high", "medium"].includes(f.priority)
              ) {
                isVisible = true;
              } else if (
                currentZoom > 10 &&
                ["superhigh", "high"].includes(f.priority)
              ) {
                isVisible = true;
              } else if (
                currentZoom > 8 &&
                ["superhigh"].includes(f.priority)
              ) {
                isVisible = true;
              }

              if (
                !isVisible &&
                zoomDirection === "in" &&
                zoomLevelBeforeMove < 14
              ) {
                isVisible = f?.show ?? false;
              }

              return { ...f, show: isVisible };
            })
          );

          for (const m of tempMarkers) {
            setMarkerVisibility(m, m.show);
          }

          tempMarkers = tempMarkers.concat(
            markers.filter((m: any) => !visibleMarkerIds.includes(m.id))
          );

          //Update markers state
          const uniqueArray = tempMarkers.filter(
            (item, index, self) =>
              index === self.findIndex((t) => t.id === item.id)
          );
          newMarkers = uniqueArray;
        }
      }

      const center = map.getCenter();
      const zoomLevel = map.getZoom();

      unhideSearchThisAreaButton();

      return {
        markers: newMarkers,
        lng: parseFloat(center.lng.toFixed(4)),
        lat: parseFloat(center.lat.toFixed(4)),
        zoom: parseFloat(zoomLevel.toFixed(2)),
      };
    }
  };

  const isGeoJsonWithCoordinates = (
    geometry: GeoJSON.Geometry
  ): geometry is GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon => {
    return (geometry as GeoJSON.Point).coordinates !== undefined;
  };

  function isGeoJsonPoint(
    geometry: any
  ): geometry is { type: "Point"; coordinates: [number, number] } {
    return (
      geometry &&
      geometry.type === "Point" &&
      Array.isArray(geometry.coordinates) &&
      geometry.coordinates.length === 2
    );
  }

  const showSearchThisAreaButton = (
    map: mapboxgl.Map,
    initialSearchViewportBounds: LngLatBounds | undefined,
    hide: boolean
  ) => {
    if (hide) return false;
    //check hide isn't out of date
    const { hideSearchThisAreaButton, allCertified } = useAppState.getState();

    if (hideSearchThisAreaButton || allCertified) return false;

    writeToLog([
      "[UseMapFunctions] running showSearchThisAreaButton",
      initialSearchViewportBounds,
      hide,
    ]);
    const currentBounds = map.getBounds();
    if (!currentBounds || !initialSearchViewportBounds) return false;

    const epsilon = 0.001; // Allow a small tolerance for floating-point precision

    // Extract corners for current bounds
    const { lat: minLatCurrent, lng: minLngCurrent } =
      currentBounds.getSouthWest();
    const { lat: maxLatCurrent, lng: maxLngCurrent } =
      currentBounds.getNorthEast();

    // Extract corners for initial search viewport bounds
    const { lat: minLatOriginal, lng: minLngOriginal } =
      initialSearchViewportBounds.getSouthWest();
    const { lat: maxLatOriginal, lng: maxLngOriginal } =
      initialSearchViewportBounds.getNorthEast();

    const show =
      !(minLatCurrent >= minLatOriginal - epsilon) || // Bottom edge
      !(minLngCurrent >= minLngOriginal - epsilon) || // Left edge
      !(maxLatCurrent <= maxLatOriginal + epsilon) || // Top edge
      !(maxLngCurrent <= maxLngOriginal + epsilon); // Right edge

    writeToLog(["[UseMapFunctions] [showSearchThisAreaButton] result", show]);
    return show;
  };

  const shouldRunZoomToSearch = (
    map: mapboxgl.Map,
    initialSearchViewportBounds: LngLatBounds | undefined
  ) => {
    writeToLog([
      "[UseMapFunctions] running shouldRunZoomToSearch",
      map,
      initialSearchViewportBounds,
    ]);

    if (!initialSearchViewportBounds) {
      const { initialSearchViewportBounds: ib } = useAppState.getState();
      initialSearchViewportBounds = ib;
    }
    const currentBounds = map.getBounds();

    if (!currentBounds || !initialSearchViewportBounds) return false;

    const epsilon = 0.001; // Allow a small tolerance for floating-point precision

    // Extract corners for current bounds
    const { lat: minLatCurrent, lng: minLngCurrent } =
      currentBounds.getSouthWest();
    const { lat: maxLatCurrent, lng: maxLngCurrent } =
      currentBounds.getNorthEast();

    // Extract corners for initial search viewport bounds
    const { lat: minLatOriginal, lng: minLngOriginal } =
      initialSearchViewportBounds.getSouthWest();
    const { lat: maxLatOriginal, lng: maxLngOriginal } =
      initialSearchViewportBounds.getNorthEast();

    // Check if currentBounds are completely within initialBounds
    const isCompletelyWithin =
      minLatCurrent >= minLatOriginal - epsilon && // Bottom edge
      minLngCurrent >= minLngOriginal - epsilon && // Left edge
      maxLatCurrent <= maxLatOriginal + epsilon && // Top edge
      maxLngCurrent <= maxLngOriginal + epsilon; // Right edge

    return isCompletelyWithin;
  };

  const searchThisArea = async (
    map: mapboxgl.Map | null,
    isSmallScreen: boolean,
    setIsSearching: (b: boolean) => void,
    setSearchResults: (results: SearchResult[]) => void,
    setUnfilteredSearchResults: (results: SearchResult[]) => void,
    pageNumber: number,
    searchSet: SearchSet,
    filterSet: FilterSet,
    incrementPageNumber: () => void,
    gender: string,
    orientation: string,
    additionalGender: string,
    setHasMoreResults: (b: boolean) => void,
    searchResults: SearchResult[],
    unfilteredSearchResults: SearchResult[],
    setShowNoResultsModal: (b: boolean) => void,
    updateMapWithSearchResults: () => void
  ) => {
    if (!map) return;

    const type = searchSet?.what ? "category" : "keyword";

    //* SEARCH-THIS-AREA
    await runSearch(
      setIsSearching,
      setSearchResults,
      setUnfilteredSearchResults,
      pageNumber,
      searchSet,
      filterSet,
      incrementPageNumber,
      gender,
      additionalGender,
      orientation,
      setHasMoreResults,
      type,
      map,
      searchResults,
      unfilteredSearchResults,
      small,
      setShowNoResultsModal,
      true,
      false,
      false,
      true
    );

    let partial: any = {};

    partial.lastSearchType = "searchThisArea";
    partial.lastMoveAction = "searchThisArea";
    partial.hideSearchThisAreaButton = true;
    partial.isSearching = false;
    setPartial(partial);

    updateMapWithSearchResults();

    // if toilets, fit bounds
    if (
      (filterSet?.selectedFeatures.some(
        (item) => item.toLowerCase() === refugeRestroomsCategory.toLowerCase()
      ) ||
        searchSet?.what === refugeRestroomsCategory) &&
      map
    ) {
      const { lastRRLngLat } = useAppState.getState();

      // get lat lng of last search
      if (lastRRLngLat) {
        setMovingAutomatically(true);
        map.flyTo({
          center: lastRRLngLat,
          zoom: 11,
          speed: 0.6,
        });

        const handleMoveEnd = () => {
          if (!map) return;
          setTimeout(() => {
            setMovingAutomatically(false);
          }, 400);
          map.off("moveend", handleMoveEnd);
        };

        map.on("moveend", handleMoveEnd);
      }
    }
  };

  const searchSmallerArea = async (
    map: mapboxgl.Map | null,
    isSmallScreen: boolean,
    setIsSearching: (b: boolean) => void,
    setSearchResults: (results: SearchResult[]) => void,
    setUnfilteredSearchResults: (results: SearchResult[]) => void,
    pageNumber: number,
    searchSet: SearchSet,
    filterSet: FilterSet,
    incrementPageNumber: () => void,
    gender: string,
    orientation: string,
    additionalGender: string,
    setHasMoreResults: (b: boolean) => void,
    searchResults: SearchResult[],
    unfilteredSearchResults: SearchResult[],
    setShowNoResultsModal: (b: boolean) => void,
    updateMapWithSearchResults: () => void
  ) => {
    if (!map) return;

    const type = searchSet?.what ? "category" : "keyword";

    // get an area to search, set searchSet.where to a location with the right attrs
    let tempSearchSet = { ...searchSet };
    const { resultsPageNumber } = useAppState.getState();
    const zoomFactor = Math.max(
      0.3,
      resultsPageNumber < 5 ? parseFloat("0." + (resultsPageNumber + 1)) : 0.9
    );
    const bounds = map.getBounds();
    if (!bounds) {
      throw Error("Cannot identify map bounds");
    }
    setMovingAutomatically(true);

    const southWest = bounds.getSouthWest();
    const northEast = bounds.getNorthEast();

    // Extract the latitudes and longitudes
    const minLat = southWest.lat;
    const minLng = southWest.lng;
    const maxLat = northEast.lat;
    const maxLng = northEast.lng;

    const newMinLat = minLat + ((maxLat - minLat) * zoomFactor) / 2;
    const newMaxLat = maxLat - ((maxLat - minLat) * zoomFactor) / 2;
    const newMinLng = minLng + ((maxLng - minLng) * zoomFactor) / 2;
    const newMaxLng = maxLng - ((maxLng - minLng) * zoomFactor) / 2;

    tempSearchSet.where = JSON.stringify({
      properties: { bbox: [newMinLng, newMinLat, newMaxLng, newMaxLat] },
    });

    //* SEARCH-THIS-AREA
    await runSearch(
      setIsSearching,
      setSearchResults,
      setUnfilteredSearchResults,
      1,
      tempSearchSet,
      filterSet,
      incrementPageNumber,
      gender,
      additionalGender,
      orientation,
      setHasMoreResults,
      type,
      map,
      searchResults,
      unfilteredSearchResults,
      small,
      setShowNoResultsModal,
      false,
      false,
      true,
      true,
      true
    );

    let partial: any = {};

    partial.resultsPageNumber = resultsPageNumber + 1;
    partial.lastSearchType = "searchThisArea";
    partial.lastMoveAction = "searchThisArea";
    partial.hideSearchThisAreaButton = true;
    partial.isSearching = false;
    setPartial(partial);
    updateMapWithSearchResults();
    setMovingAutomatically(false);
  };

  const setLocationPopUpForSearchBoxResult = async (
    searchSet: SearchSet,
    map: mapboxgl.Map,
    popupContainerRef: React.MutableRefObject<HTMLDivElement | null>,
    addLocationsToMap: (
      geojson: GeoJSON.FeatureCollection,
      skipFlyTo: boolean
    ) => Promise<CustomMarker[] | undefined>,
    openPopUp: (
      popupContainerRef: React.MutableRefObject<HTMLDivElement | null>,
      locationId: number,
      feature?: any,
      localMarkers?: CustomMarker[],
      localMarker?: Marker
    ) => void,
    isSmallScreen: boolean,
    closeSearchDialog: () => void,
    genderId: number | null,
    orientationId: number | null,
    additionalGenderId: number | null
  ) => {
    try {
      writeToLog([
        "[UseMapFunctions]",
        "calling setLocationPopUpForSearchBoxResult",
        JSON.stringify(searchSet?.keywordSearchBoxSelection),
      ]);

      if (searchSet?.keywordSearchBoxSelection) {
        if (!map) return;

        if (!popupContainerRef.current) {
          const container = document.createElement("div");
          popupContainerRef.current = container;
          popupContainerRef.current.setAttribute("id", "popup-container");
          popupContainerRef.current.style.width = "fit-content";
          document.body.appendChild(popupContainerRef.current);
        }

        // add to map if it's a poi
        let feature = { ...searchSet?.keywordSearchBoxSelection } as any;

        feature.properties = {
          ...feature.properties,
          id: -1,
          show: true,
          address:
            feature.properties?.address +
            ", " +
            feature.properties?.place_formatted,
        };

        if (!runInDummyDataMode) {
          const { data, error } = await listLocations({
            client: shaneClient,
            query: {
              mapboxId: feature.properties?.mapbox_id,
            },
          });

          writeToLog([
            "[UseMapFunctions] [setLocationPopUpForSearchBoxResult] location from database",
            data,
            error,
          ]);

          let isShaneLocation = false;

          if (data && data.data) {
            isShaneLocation = true;
            const result = data?.data[0];

            //@ts-ignore
            let tags =
              (result?.tags ?? []).length === 0 ? [] : (result?.tags as any[]);
            let mapboxTags = feature.properties?.poi_category.map(
              (c: string) => {
                return { label: c, color: tealBackground, textColor: tealText };
              }
            ) as any;
            tags = mapboxTags.concat(tags);

            feature.properties = {
              ...feature.properties,
              id: result.locationId,
              isCertified: getTinyIntAsBoolean(result?.isCertified ?? false),
              isPromoted: getTinyIntAsBoolean(result?.isPromoted ?? false),
              isShaneLocation: true,
              mapboxId: feature.properties?.mapbox_id,
              tags: tags.length === 0 ? mapboxTags : tags,
              category: result.category?.name,
            };

            if (result?.locationId) {
              const { data } = await fetchLocationRatingSummaries({
                client: shaneClient,
                path: {
                  locationId: result.locationId,
                },
                query: {
                  genderId:
                    typeof genderId === "string"
                      ? parseInt(genderId)
                      : genderId ?? undefined,
                  orientationId:
                    typeof orientationId === "string"
                      ? parseInt(orientationId)
                      : orientationId ?? undefined,
                  additionalGenderId:
                    typeof additionalGenderId === "string"
                      ? parseInt(additionalGenderId)
                      : additionalGenderId ?? undefined,
                },
              });

              setSelectedFeature(feature);

              writeToLog([
                "[UseMapFunctions] [setLocationPopUpForSearchBoxResult] fetchLocationRatingSummaries result",
                data,
                error,
              ]);

              if (data) {
                const allPerspectivesRating = data.find(
                  (d: any) => d.question === "Overall" && d.type === "everyone"
                );
                let ratings: Rating[] = [];

                if (allPerspectivesRating) {
                  ratings = [
                    {
                      who: "All perspectives",
                      rating: parseInt(
                        allPerspectivesRating.averageResponse ?? "0"
                      ),
                    },
                  ];
                }

                if (genderId || orientationId || additionalGenderId) {
                  const peopleLikeYouRating = data.find(
                    (d: any) =>
                      d.question === "Overall" && d.type === "people like you"
                  );

                  if (peopleLikeYouRating) {
                    ratings.push({
                      who: "Similar identities",
                      rating: parseInt(
                        peopleLikeYouRating.averageResponse ?? "0"
                      ),
                    });
                  } else {
                    ratings.push({
                      who: "Similar identities",
                      rating: "-",
                    });
                  }
                }

                feature.properties = {
                  ...feature.properties,
                  ratings,
                };
              }
            }
          }

          if (error) {
            //*treat as !isShaneLocation
            isShaneLocation = false;
          }

          if (!isShaneLocation) {
            //TODO work out shane category from poi
            const { data } = await fetchCategoryForPoiCat({
              client: shaneClient,
              path: {
                poiCatName: feature?.properties?.poi_category[0],
              },
            });

            feature.properties = {
              ...feature.properties,
              id: feature.properties?.mapbox_id,
              isCertified: false,
              isPromoted: false,
              isShaneLocation: false,
              mapboxId: feature.properties?.mapbox_id,
              tags: [],
              category: data ? data[0].categoryName : "",
            };
          }
        } else {
          const dummyResult = dummySearchResults.find(
            (r: any) => r.locationName === feature?.properties?.name
          );

          if (dummyResult) {
            feature.properties = {
              ...feature.properties,
              id: dummyResult.locationId,
              address: dummyResult.locationAddress,
              ratings: dummyResult.ratings,
              isCertified: dummyResult.isCertified,
              isPromoted: dummyResult.isPromoted,
              isShaneLocation: dummyResult.isShaneLocation,
              mapboxId: dummyResult.mapboxId,
              tags: dummyResult.tags,
              category: dummyResult.locationCategory,
              description: dummyResult.locationDescription,
            };
          }
        }
        setSelectedFeature(feature);

        //SECTION update map
        writeToLog(
          "[UseMapFunctions][ setLocationPopUpForSearchBoxResult] calling addlocationstomap"
        );

        const localMarkers = await addLocationsToMap(
          {
            type: "FeatureCollection",
            features: [feature],
          },
          false
        );

        if (!localMarkers) return;
        //!SECTION

        const { resultsPosition } = useAppState.getState();

        //collapse search if sm-
        if (small) {
          closeSearchDialog();

          if (resultsPosition === "collapsed") {
            setResultsPosition("middle");
          }
        }

        writeToLog(
          "[UseMapFunctions] [setLocationPopUpForSearchBoxResult] calling openpopup"
        );

        openPopUp(
          popupContainerRef,
          feature?.properties?.id,
          feature,
          localMarkers,
          localMarkers[0].marker
        );
      }
    } catch (e) {}
  };

  const runDummySearch = (
    isBathrooms: boolean,
    setIsSearchingTrue: () => void,
    setSearchResults: (searchResults: SearchResult[]) => void
  ) => {
    setIsSearchingTrue();
    setTimeout(() => {
      let results = (
        isBathrooms ? dummyBathroomData : dummySearchResults
      ).slice(0, pageSize);

      results = results.sort((a, b) => {
        // 1. Sort by isPromoted (true first)
        if (a.isPromoted !== b.isPromoted) {
          return a.isPromoted ? -1 : 1;
        }

        if (a.ratings) {
          let aRating =
            a.ratings?.find((r) => r.who === "All perspectives")?.rating ?? 0;
          let bRating =
            b.ratings?.find((r) => r.who === "All perspectives")?.rating ?? 0;

          if (typeof aRating === "string") {
            aRating = 0;
          }

          if (typeof bRating === "string") {
            bRating = 0;
          }

          // 2. Within isPromoted, sort by rating (highest to lowest)
          if (aRating !== bRating) {
            return bRating - aRating;
          }
        }

        // 3. If ratings are the same, sort by isCertified (true first)
        if (a.isCertified !== b.isCertified) {
          return a.isCertified ? -1 : 1;
        }

        // 4. Finally, push items where isShaneLocation is false to the end
        if (a.isShaneLocation !== b.isShaneLocation) {
          return a.isShaneLocation ? -1 : 1;
        }

        return 0; // No difference, keep order
      });

      //@ts-ignore
      setSearchResults(results);
    }, 1500);
  };

  const [isRunning, setIsRunning] = useState<boolean>(false);

  const runSearch = async (
    setIsSearching: (b: boolean) => void,
    setSearchResults: (searchResults: SearchResult[]) => void,
    setUnfilteredSearchResults: (searchResults: SearchResult[]) => void,
    pageNumber: number,
    searchSet: SearchSet,
    filterSet: FilterSet,
    incrementPageNumber: () => void,
    gender: string | null,
    additionalGender: string | null,
    orientation: string | null,
    setHasMoreResults: (b: boolean) => void,
    type: "category" | "keyword",
    map: mapboxgl.Map | null,
    searchResults: SearchResult[],
    unfilteredSearchResults: any[],
    isSmallScreen: boolean,
    setShowNoResultsModal: (show: boolean) => void,
    forceViewportSearch?: boolean,
    searchQuietly?: boolean,
    doNotFallbackToCertified?: boolean,
    isAutomatedSearch?: boolean,
    hideNoResultsFoundToast?: boolean
  ) => {

    if (isRunning) return;
    setIsRunning(true);
    const {
      isSmallScreen: small,
      setPreloadedGeoJson,
      markersRef,
      preloadedGeoJson,
      setMarkersRef,
    } = useAppState.getState();

    if (preloadedGeoJson) {
      setPreloadedGeoJson(null);
      if (markersRef.current) {
        //remove preloaded locations from map
        for (const m of markersRef.current) {
          if (document.body.contains(m.marker.getElement())) m.marker.remove();
        }
        setMarkersRef([]);
      }
    }

    writeToLog([
      "[UseMapFunctions] running runSearch",
      searchSet,
      isAutomatedSearch,
      map,
      type,
    ]);
    const isBathrooms = searchSet.what === refugeRestroomsCategory;

    if (runInDummyDataMode) {
      runDummySearch(isBathrooms, () => setIsSearching(true), setSearchResults);
    } else {
      try {
        let query: SearchByCategoryOrKeywordData["query"] = {
          gender: gender ?? "",
          additionalGender: additionalGender ?? "",
          orientation: orientation ?? "",
          pageSize,
          pageNumber,
        };

        if (!searchQuietly) setIsSearching(true);

        let tempSearchResults: any[] = [];

        let maxPageNumber = 0;

        // get results for page
        if (type === "category" && searchSet.where === mapHiddenWhereCategory) {
          //do an online only search
          const { data, error } = await searchForOnlineOnly({
            client: shaneClient,
            query: {
              categoryName:
                ((searchSet?.whatSub && searchSet.whatSub) !== "Search all"
                  ? searchSet.whatSub
                  : searchSet.what) ?? undefined,
              orientation: orientation ?? "",
              gender: gender ?? "",
              additionalGender: additionalGender ?? "",
              pageSize,
              pageNumber,
            },
          });

          if (error) {
            if (((error as any)?.error_message ?? "") !== "No records found") {
              throw Error(
                (error as any)?.error_message ??
                  (error as any)?.message ??
                  "Server error when searching for locations"
              );
            }
          }

          if (data && data?.data) {
            tempSearchResults = data?.data.map((d: any) => {
              return {
                ...d,
                ratings: [
                  {
                    who: "All perspectives",
                    rating:
                      d?.ratingAll && !isNaN(parseFloat(d?.ratingAll))
                        ? parseFloat(d?.ratingAll)
                        : "-",
                  },
                  {
                    who: "Similar identities",
                    rating:
                      d?.ratingLikeMe && !isNaN(parseFloat(d?.ratingLikeMe))
                        ? parseFloat(d?.ratingLikeMe)
                        : "-",
                  },
                ],
              };
            });
            //! this is online only, so normal paging applies
            maxPageNumber = -1;
          }
        } else {
          // keyword or category search


          if (searchSet?.poiCategories && searchSet?.poiCategories.length > 0) {
            query.poiCategoryUUIDs = searchSet?.poiCategories
              .map((s: any) => s.canonicalIds)
              .join(",");
          } else if (type === "category") {
            query.poiCategoryUUIDs = "";
          }



          if (searchSet?.where === "Near me") {
            //get stored lat lng
            const homeString = localStorage.getItem(
              locationPermissionsLocalStorageKey
            );

            if (!homeString) {
              throw Error("Cannot find home coordinates");
            }

            const loc = JSON.parse(homeString);

            query.lng = loc.lng;
            query.lat = loc.lat;
          } else {
            // use minmaxlatlng
            if (
              (searchSet?.where === "This area" ||
                (searchSet?.where ?? "").startsWith("{")) &&
              isBathrooms
            ) {
              // if isBathrooms and pageNumber > 1, should use original lat lng
              if (pageNumber > 1 && initialRRLngLat && !forceViewportSearch) {
                //TODO should only do this if we're not doing a search-this-area
                //@ts-ignore
                query.lng = initialRRLngLat.lng;
                //@ts-ignore
                query.lat = initialRRLngLat.lat;
              } else if ((searchSet?.where ?? "").startsWith("{")) {
                //convert minmaxlatlng to lat lng
                const loc = JSON.parse(searchSet?.where ?? "{}");
                const bbox = loc?.properties?.bbox;
                if (!bbox) {
                  const coords = loc?.geometry?.coordinates
                    ? loc?.geometry?.coordinates
                    : loc?.properties?.coordinates;

                  query.lng = coords[0];
                  query.lat = coords[1];
                } else {
                  //find center point
                  const minLng = bbox[0];
                  const minLat = bbox[1];
                  const maxLng = bbox[2];
                  const maxLat = bbox[3];

                  query.lat = (minLat + maxLat) / 2;
                  query.lng = (minLng + maxLng) / 2;
                }

                query.country = loc.properties.context.country.country_code;

                const { lastSearchType } = useAppState.getState();

                let partial: any = {};

                if (!lastSearchType) {
                  partial.initialRRLngLat = {
                    lng: query.lng ?? 0,
                    lat: query.lat ?? 0,
                  };
                }
                partial.lastRRLngLat = {
                  lng: query.lng ?? 0,
                  lat: query.lat ?? 0,
                };

                setPartial(partial);
              } else {
                if (!map) {
                  throw Error("Map not found");
                }

                const bounds = map.getBounds();
                if (!bounds) {
                  throw Error("Cannot identify map bounds");
                }

                const southWest = bounds.getSouthWest();
                const northEast = bounds.getNorthEast();

                // Extract the latitudes and longitudes
                const minLat = southWest.lat;
                const minLng = southWest.lng;
                const maxLat = northEast.lat;
                const maxLng = northEast.lng;

                query.lat = (minLat + maxLat) / 2;
                query.lng = (minLng + maxLng) / 2;

                const { lastSearchType } = useAppState.getState();

                let partial: any = {};

                if (!lastSearchType) {
                  partial.initialRRLngLat = {
                    lng: query.lng ?? 0,
                    lat: query.lat ?? 0,
                  };
                }
                partial.lastRRLngLat = {
                  lng: query.lng ?? 0,
                  lat: query.lat ?? 0,
                };

                setPartial(partial);
              }
            } else if (
              (type === "keyword" && searchSet?.where === "Anywhere") ||
              searchSet?.where === "This area" ||
              forceViewportSearch
            ) {
              // current viewport
              if (!map) {
                throw Error("Map not found");
              }
              const bounds = map.getBounds();
              if (!bounds) {
                throw Error("Cannot identify map bounds");
              }

              const southWest = bounds.getSouthWest();
              const northEast = bounds.getNorthEast();

              // Extract the latitudes and longitudes
              query.minLat = southWest.lat;
              query.minLng = southWest.lng;
              query.maxLat = northEast.lat;
              query.maxLng = northEast.lng;
            } else if (searchSet?.where === "Anywhere") {
              // get the bbox for the whole world?
              query.minLng = -180;
              query.minLat = -90;
              query.maxLng = 180;
              query.maxLat = 90;
            } else {

              // specific location
              const loc = JSON.parse(
                searchSet?.where ??
                  JSON.stringify(searchSet?.keywordSearchBoxSelection) ??
                  "{}"
              );

              const bbox = loc?.properties?.bbox;

              if (!bbox) {
                //!happens when the loc is a street
                let coords = loc?.properties?.geometry?.coordinates;

                if (coords) {
                  query.lng = coords[0];
                  query.lat = coords[1];
                } else {
                  coords = loc?.properties?.coordinates;
                  if (coords) {
                    query.lng = coords.longitude;
                    query.lat = coords.latitude;
                  } else {
                    //set to current viewport
                    if (!map) return;
                    const bounds = map.getBounds();
                    if (!bounds) {
                      throw Error("Cannot identify map bounds");
                    }

                    const southWest = bounds.getSouthWest();
                    const northEast = bounds.getNorthEast();

                    // Extract the latitudes and longitudes
                    const minLat = southWest.lat;
                    const minLng = southWest.lng;
                    const maxLat = northEast.lat;
                    const maxLng = northEast.lng;

                    query.lat = (minLat + maxLat) / 2;
                    query.lng = (minLng + maxLng) / 2;
                  }
                }
              } else {
                query.minLng = bbox[0];
                query.minLat = bbox[1];
                query.maxLng = bbox[2];
                query.maxLat = bbox[3];
              }

              if (loc?.properties?.context?.country)
                query.country = loc.properties.context.country.country_code;


            }
          }

          if (isBathrooms) {
            //add in RR-only params - unisex + accessible
            //TODO - check whether this should be using .features or .selectedFeatures
            if ((filterSet?.features ?? []).includes("Accessible"))
              query.accessible = "1";
            if (
              (filterSet?.features ?? []).includes("Gender-inclusive toilets")
            )
              query.unisex = "1";
          }

          if (type === "keyword") {
            if (searchSet?.where && !searchSet?.keyword) {
              //change to a shane-only search
              searchSet.what = "All Shane-Rated Places";
            } else {
              if (!searchSet?.keyword) {
                throw Error("Cannot do a keyword search without a keyword!");
              }
              query.text = searchSet.keyword;
            }
          } else {

            query.categoryName =
              searchSet?.what === "Shane Certified"
                ? searchSet?.what
                : (searchSet?.whatSub &&
                  searchSet.whatSub !== "Search all" &&
                  !isBathrooms
                    ? searchSet?.whatSub
                    : searchSet?.what) ?? "All Shane-Rated Places";
          }

          writeToLog([
            "[UseMapFunctions] [runSearch] final search query",
            JSON.stringify(query),
          ]);

          if (searchSet?.what === "Shane Certified") {
            query = { ...query, pageSize: 1000 };

            const { data, error } = await searchForCertifiedOnly({
              client: shaneClient,
              query,
            });

            if (error) {
              if (
                ((error as any)?.error_message ?? "") !== "No records found"
              ) {
                throw Error(
                  (error as any)?.error_message ??
                    (error as any)?.message ??
                    "Server error when searching for locations"
                );
              }
            }

            if (data && (data as any)?.data) {
              writeToLog(["[UseMapFunctions] [runSearch] data found", data]);
              tempSearchResults = (data as any)?.data.map((d: any) => {
                return {
                  ...d,
                  ratings: [
                    {
                      who: "All perspectives",
                      rating:
                        d?.ratingAll && !isNaN(parseFloat(d?.ratingAll))
                          ? parseFloat(d?.ratingAll)
                          : "-",
                    },
                    {
                      who: "Similar identities",
                      rating:
                        d?.ratingLikeMe && !isNaN(parseFloat(d?.ratingLikeMe))
                          ? parseFloat(d?.ratingLikeMe)
                          : "-",
                    },
                  ],
                };
              });

              maxPageNumber = -1;
            }
          } else if (searchSet?.what === "All Shane-Rated Places") {
            query = { ...query, pageSize: 1000 };

            const { data, error } = await searchForShaneRatedOnly({
              client: shaneClient,
              query,
            });

            if (error) {
              if (
                ((error as any)?.error_message ?? "") !== "No records found"
              ) {
                throw Error(
                  (error as any)?.error_message ??
                    (error as any)?.message ??
                    "Server error when searching for locations"
                );
              }
            }

            if (data?.meta?.countryFilterApplied === 1) {
              //* pop a toast
              //use specific error message
              toast.info(
                <div
                  dangerouslySetInnerHTML={{ __html: resultsFilteredError }}
                />,
                {
                  hideProgressBar: true,
                  transition: Slide,
                  position: small ? "top-center" : "top-right",
                  pauseOnHover: true,
                  autoClose: 7000,
                }
              );
            }

            if (data && (data as any)?.data) {
              writeToLog(["[UseMapFunctions] [runSearch] data found", data]);
              tempSearchResults = (data as any)?.data.map((d: any) => {
                return {
                  ...d,
                  ratings: [
                    {
                      who: "All perspectives",
                      rating:
                        d?.ratingAll && !isNaN(parseFloat(d?.ratingAll))
                          ? parseFloat(d?.ratingAll)
                          : "-",
                    },
                    {
                      who: "Similar identities",
                      rating:
                        d?.ratingLikeMe && !isNaN(parseFloat(d?.ratingLikeMe))
                          ? parseFloat(d?.ratingLikeMe)
                          : "-",
                    },
                  ],
                };
              });

              maxPageNumber = -1;
            }
          } else {
            const { data, error } = await searchByCategoryOrKeyword({
              client: shaneClient,
              query,
            });

            if (error) {
              if (
                (error as any).error_message ===
                "No location available for specified country"
              ) {
                //* pop a toast
                //use specific error message
                let placeType = "";
                try {
                  placeType =
                    JSON.parse(searchSet?.where ?? "{}")?.properties
                      ?.feature_type ?? "place";
                } catch (e) {
                  placeType = "place";
                }
                let message = resultsBlockedError.replace(
                  "###PLACE_TYPE###",
                  placeType
                );

                toast.error(
                  <div dangerouslySetInnerHTML={{ __html: message }} />,
                  {
                    hideProgressBar: true,
                    transition: Slide,
                    position: small ? "top-center" : "top-right",
                    pauseOnHover: true,
                    autoClose: 7000,
                  }
                );
                // skipToast = true;
              } else {
                if (
                  ((error as any)?.error_message ?? "") !== "No records found"
                ) {
                  throw Error(
                    (error as any)?.error_message ??
                      (error as any)?.message ??
                      "Server error when searching for locations"
                  );
                }
              }
            }

            if (data && (data as any)?.data) {
              writeToLog(["[UseMapFunctions] [runSearch] data found", data]);

              if (
                (data as any)?.data.length === 1 &&
                Object.keys((data as any)?.data[0]).length === 0 &&
                !(hideNoResultsFoundToast ?? false)
              ) {
                //no results
                throw Error("No results found");
              }

              tempSearchResults = (data as any)?.data.map((d: any) => {
                return {
                  ...d,
                  ratings: [
                    {
                      who: "All perspectives",
                      rating:
                        d?.ratingAll && !isNaN(parseFloat(d?.ratingAll))
                          ? parseFloat(d?.ratingAll)
                          : "-",
                    },
                    {
                      who: "Similar identities",
                      rating:
                        d?.ratingLikeMe && !isNaN(parseFloat(d?.ratingLikeMe))
                          ? parseFloat(d?.ratingLikeMe)
                          : "-",
                    },
                  ],
                };
              });

              if (isBathrooms) {
                //! this is rr, so normal paging applies
                maxPageNumber = -1;
              } else {
                maxPageNumber = 999; // data.meta?.totalMbPagesAvailable ?? -1;
              }
            }
          }
        }

        tempSearchResults = tempSearchResults.map((r) => {
          return {
            ...r,
            mapboxId: r.mapboxId ?? r.locationId,
            tags: r?.tags ?? r?.locationTags,
          };
        });

        let newFilterSet = { ...filterSet };

        if (tempSearchResults.length === 0) {
          if (isAutomatedSearch) {
            let partial: any = {};

            partial.isSearching = false;
            partial.hideSearchThisAreaButton = true;

            setPartial(partial);
            if (hideNoResultsFoundToast ?? false) {
              throw Error("");
            }
            throw Error("No results found in this area");
          }

          if (
            searchSet?.what !== "Shane Certified" &&
            !doNotFallbackToCertified
          ) {
            newFilterSet = {
              hours: "Any time",
              selectedFeatures: [],
              selectedFilters: [],
            };
            let partial: any = {};
            partial.filterSet = newFilterSet;

            writeToLog([
              "[UseMapFunctions] [runSearch] falling back to certified in current location",
            ]);

            partial.isSearching = false;
            partial.showNoResultsModal = true;
            partial.hideSearchThisAreaButton = true;
            partial.isSearching = false;
            setIsRunning(false);

            setPartial(partial);

            return;
          }

          if (
            searchSet?.what === "Shane Certified" &&
            searchSet?.where !== "Anywhere"
          ) {
            const newSearchSet = { ...searchSet, where: "Anywhere" };
            let partial: any = {};

            partial.searchSet = newSearchSet;

            newFilterSet = {
              hours: "Any time",
              selectedFeatures: [],
              selectedFilters: [],
            };
            partial.filterSet = newFilterSet;

            setPartial(partial);

            writeToLog(
              "[UseMapFunctions] [runSearch] falling back to certified in any location"
            );

            // call self again and return
            await runSearch(
              setIsSearching,
              setSearchResults,
              setUnfilteredSearchResults,
              pageNumber,
              newSearchSet,
              newFilterSet,
              incrementPageNumber,
              gender,
              additionalGender,
              orientation,
              setHasMoreResults,
              type,
              map,
              searchResults,
              unfilteredSearchResults,
              small,
              setShowNoResultsModal,
              forceViewportSearch ?? false,
              searchQuietly ?? false,
              true,
              isAutomatedSearch ?? false
            );
            return;
          }
        }

        let partial: any = {};

        if (pageNumber < maxPageNumber && maxPageNumber > 0) {
          partial.hasMoreResults = true;
        } else if (
          maxPageNumber === -1 &&
          tempSearchResults.length === pageSize
        ) {
          partial.hasMoreResults = true;
        } else {
          partial.hasMoreResults = false;
        }
        if (type === "keyword") partial.hasMoreResults = false;

        partial.unfilteredSearchResults =
          unfilteredSearchResults.concat(tempSearchResults);

        setPartial(partial);

        searchResults = searchResults.concat(tempSearchResults);

        incrementPageNumber();

        //*filter down results if any filters are applied, and remove duplicates
        const res = await applyFilters(
          searchResults,
          newFilterSet,
          setIsSearching,
          setSearchResults,
          small,
          searchQuietly
        );

        let partial1: any = {};

        if (res.length > 0) {
          const { resultsPosition } = useAppState.getState();
          partial1.drawerContentLoaded = true;
          if (resultsPosition === "collapsed")
            partial1.resultsPosition = "middle";
        }
        partial1.isSearching = false;
        setPartial(partial1);
        setIsRunning(false);
        return res;
      } catch (e: any) {
        console.error("[UseMapFunctions] [runSearch]", e);
        if (!searchQuietly || isAutomatedSearch) {
          if (e.message.length > 0) {
            toast.error(e.message, {
              hideProgressBar: true,
              transition: Slide,
              position: small ? "top-center" : "top-right",
            });
          }
          let partial: any = {};
          partial.isSearching = false;
          setPartial(partial);
          setIsRunning(false);
        }
      }
    }
  };

  const loadMoreDummy = (
    setLoadingMore: (b: boolean) => void,
    //@ts-ignore
    setSearchResults: (searchResults: SearchResult[]) => void,
    searchResults: SearchResult[]
  ) => {
    setLoadingMore(true);
    setTimeout(() => {
      let partial: any = {};
      partial.searchResults =
        //@ts-ignore
        dummySearchResults.slice(0, searchResults.length + pageSize);
      partial.loadingMore = false;
      setPartial(partial);
    }, 250);
  };

  const loadMore = async (
    setLoadingMore: (b: boolean) => void,
    setSearchResults: (searchResults: SearchResult[]) => void,
    setUnfilteredSearchResults: (searchResults: SearchResult[]) => void,
    searchResults: SearchResult[],
    unfilteredSearchResults: any[],
    pageNumber: number,
    searchSet: SearchSet,
    filterSet: FilterSet,
    setHasMoreResults: (b: boolean) => void,
    incrementPageNumber: () => void,
    setIsSearching: (b: boolean) => void,
    gender: string,
    additionalGender: string,
    orientation: string,
    type: "keyword" | "category",
    map: mapboxgl.Map | null,
    isSmallScreen: boolean,
    setShowNoResultsModal: (show: boolean) => void,
    updateMapWithSearchResults: (skipFlyTo: boolean) => void
  ) => {
    if (runInDummyDataMode) {
      loadMoreDummy(setLoadingMore, setSearchResults, searchResults);
      setHasMoreResults(dummySearchResults.length > searchResults.length);
    } else {
      await runSearch(
        setIsSearching,
        setSearchResults,
        setUnfilteredSearchResults,
        pageNumber,
        searchSet,
        filterSet,
        incrementPageNumber,
        gender,
        additionalGender,
        orientation,
        setHasMoreResults,
        type,
        map,
        searchResults,
        unfilteredSearchResults,
        small,
        setShowNoResultsModal
      );

      updateMapWithSearchResults(false);
    }
  };

  const applyFilters = async (
    searchResults: SearchResult[],
    filterSet: FilterSet,
    setIsSearching: (b: boolean) => void,
    setSearchResults: (results: any[]) => void,
    isSmallScreen: boolean,
    searchQuietly?: boolean
  ) => {
    try {
      writeToLog([
        "[UseMapFunctions] running applyFilters",
        searchResults,
        filterSet,
        small,
        searchQuietly,
      ]);
      let partial: any = {};
      if (
        JSON.stringify(filterSet) === "{}" ||
        ((filterSet?.selectedFeatures ?? []).length === 0 &&
          (filterSet?.selectedFilters ?? []).length === 0) ||
        searchResults.length === 0
      ) {
        //* no searchresults or no filters
        searchResults = searchResults.filter(
          (item, index, self) =>
            index ===
            self.findIndex((obj) => obj.locationId === item.locationId)
        );

        partial.searchResults = searchResults;
        partial.isSearching = false;

        if (searchResults.length === 0) {
          setPartial(partial);
          throw Error(
            "No results found, try changing your search terms to see more"
          );
        }
      } else if (
        (filterSet?.selectedFeatures ?? []).length === 0 &&
        (filterSet?.selectedFilters ?? []).length === 0
      ) {
        //* no filters
        partial.searchResults = searchResults;
        partial.isSearching = false;
      } else {
        if (!searchQuietly) setIsSearching(true);

        //* selectedFilters is a subset of selectedFeatures + if hours === 'Open Now', so can ignore and just use the other attributes
        const minimumRating = filterSet?.minimumRating ?? 0; //? does this apply to allPerspectives or peopleLikeMe??
        const features = filterSet?.selectedFeatures.filter(
          (f: string) => f !== "Shane certified"
        );

        const subfeatureNames = subfeatures.map((a: any) => {
          return a.option;
        });

        let tags =
          features && features.length > 0
            ? features
                .filter((a: string) => !subfeatureNames.includes(a))
                .map((f: string) => {
                  return f.replaceAll("*", "").toLowerCase();
                })
            : [];

        // ensure we're using the right tag names for the subfeatures
        const selectedSubfeatureNames =
          features &&
          features.length > 0 &&
          features.filter((a: string) => subfeatureNames.includes(a));

        if (selectedSubfeatureNames) {
          tags = tags.concat(
            subfeatures
              .filter((a: any) => selectedSubfeatureNames.includes(a.option))
              .map((a: any) => {
                return a.tag;
              })
          );
        }

        const isShaneCertified = (filterSet?.selectedFeatures ?? []).includes(
          "Shane certified"
        );

        searchResults = searchResults.filter((r) => {
          let tagLabels = (r?.tags ?? []).map((tag: any) =>
            tag.label.toLowerCase()
          );

          if (r?.source || r.locationCategory === "Gender-Inclusive Toilets")
            tagLabels.push("gender-inclusive toilets");

          return (
            (isShaneCertified ? getTinyIntAsBoolean(r.isCertified) : true) &&
            tags &&
            tags.every((required: string) => tagLabels.includes(required)) &&
            (minimumRating > 0
              ? (r?.ratings ?? []).some(
                  (rating: any) => rating.rating >= minimumRating
                )
              : true)
          );
        });
      }

      //remove dups
      searchResults = searchResults.filter(
        (item, index, self) =>
          index === self.findIndex((obj) => obj.locationId === item.locationId)
      );

      // reorder the results
      searchResults = searchResults.sort((a, b) => {
        // 1. Sort by isPromoted (true first)
        if (a.isPromoted !== b.isPromoted) {
          return a.isPromoted ? -1 : 1;
        }

        if (a.ratings) {
          let aRating =
            a.ratings?.find((r: any) => r.who === "All perspectives")?.rating ??
            0;
          let bRating =
            b.ratings?.find((r: any) => r.who === "All perspectives")?.rating ??
            0;

          if (typeof aRating === "string") {
            aRating = 0;
          }

          if (typeof bRating === "string") {
            bRating = 0;
          }

          // 2. Within isPromoted, sort by rating (highest to lowest)
          if (aRating !== bRating) {
            return bRating - aRating;
          }
        }

        // 3. If ratings are the same, sort by isCertified (true first)
        if (a.isCertified !== b.isCertified) {
          return a.isCertified ? -1 : 1;
        }

        // 4. Finally, push items where isShaneLocation is false to the end
        if (a.isShaneLocation !== b.isShaneLocation) {
          return a.isShaneLocation ? -1 : 1;
        }

        return 0; // No difference, keep order
      });

      partial.searchResults = searchResults;
      partial.isSearching = false;

      if (searchResults.length === 0) {
        throw Error(
          "No results found, try changing your search terms to see more"
        );
      }

      setPartial(partial);

      return searchResults;
    } catch (e: any) {
      toast.error(e.message, {
        hideProgressBar: true,
        transition: Slide,
        position: small ? "top-center" : "top-right",
      });
      setIsSearching(false);
      return [];
    }
  };

  //TODO HANDLE THE MANY POSSIBLE EDGE CASES
  const parseKeywordInput = async (keyword: string): Promise<any[]> => {
    const words = keyword.split(" ");

    let output: any[] = [];

    // match against poi categories
    const { data } = await listPoiCategories({ client: shaneClient }); //TODO cache these locally during a session

    if (data) {
      const poiCategories = data?.data;
      for (let w of words) {
        if (w.toLowerCase() === "hotels") {
          w = "hotel";
        }

        //* skip this word if it's a filler word
        if (!["in", "near", "nearby", "by", "to", "next"].includes(w)) {
          const foundCategory = poiCategories?.find(
            (p: any) =>
              p.name.toLowerCase().includes(w.toLowerCase()) ||
              p.canonicalId.toLowerCase().includes(w.toLowerCase())
          );

          if (foundCategory) {
            output.push({ word: w, poiCategory: foundCategory });
          } else {
            // check to see if it's a location by doing whatever search the searchbox does

            // eslint-disable-next-line
            const { data, error } = await mapboxSuggest({
              client: shaneClient,
              query: { keyword: w },
            }); //! we might need to combine some of the non category keywords into a single search as we only want to end up with a single category

            if (data) {
              output.push({ word: w, location: data });
            } else {
              output.push({ word: w });
            }
            //TODO handle error
          }
        }
      }

      const locations = output.filter((o: any) => o?.location);

      if (
        locations.length === output.length //&& output.length > 1
      ) {
        //everything's a location, which isn't right (unl), just do a normal keyword search

        return [{ word: keyword }];
      }

      if (locations.length > 1) {
        // merge locations into single string and run suggest again
        const mergedKeyword = locations
          .map((l: any) => {
            return l.word;
          })
          .join(" ");

        // eslint-disable-next-line
        const { data, error } = await mapboxSuggest({
          client: shaneClient,
          query: {
            keyword: mergedKeyword,
          },
        });

        //TODO handle error

        if (data) {
          output = output
            .filter((o: any) => !o?.location)
            .concat([{ word: mergedKeyword, location: data }]);
        }
      }
    }

    return output;
  };

  const jumpToTopOfResults = () => {
    const div = document.getElementById(
      "locationRatingsGroupVirtualizedWrapper"
    );

    if (div) {
      setTimeout(() => {
        div.children[0].scrollTo({ top: 0, behavior: "smooth" });
      }, 200);
    }
  };

  return {
    jumpToTopOfResults,
    parseKeywordInput,
    applyFilters,
    loadMore,
    loadMoreDummy,
    runSearch,
    runDummySearch,
    setLocationPopUpForSearchBoxResult,
    searchThisArea,
    shouldRunZoomToSearch,
    handleMapMove,
    isGeoJsonPoint,
    isGeoJsonWithCoordinates,
    showSearchThisAreaButton,
    searchResultsToGeoJson,
    setMarkerVisibility,
    searchSmallerArea,
  };
}
